import { useRef, useState } from 'react';
import { useAuftragCtx } from './AuftragLayout.jsx';
import { useCreateKommentar, useKommentare } from '../hooks/data.js';
import { Stand } from '../components/Stand.jsx';
import { useToast, withHinweis } from '../state/toast.jsx';
import { fileUrl } from '../lib/api.js';
import { dateTimeLabel, initials } from '../lib/format.js';
import { photoName, prepareUpload } from '../lib/files.js';
import { Icon } from '../components/Icon.jsx';
import { FileViewer } from '../sheets/FileViewer.jsx';

export function Comments() {
  const { nr } = useAuftragCtx();
  const kommentare = useKommentare(nr);
  const create = useCreateKommentar(nr);
  const toast = useToast();
  const fileInput = useRef(null);
  const [text, setText] = useState('');
  const [foto, setFoto] = useState(null); // { fileName, base64, preview }
  const [error, setError] = useState('');
  const [viewer, setViewer] = useState(null);

  const onFoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const up = await prepareUpload(file);
    setFoto({ ...up, fileName: photoName('Kommentar'), preview: `data:image/jpeg;base64,${up.base64}` });
  };

  const send = async (e) => {
    e.preventDefault();
    setError('');
    if (!text.trim() && !foto) { setError('Text oder Foto hinzufügen.'); return; }
    try {
      const res = await create.mutateAsync({ text: text.trim(), foto: foto ? { fileName: foto.fileName, base64: foto.base64 } : undefined });
      setText('');
      setFoto(null);
      toast(res?.hinweis
        ? withHinweis('Kommentar gespeichert', res.hinweis)
        : { text: foto ? 'Kommentar gespeichert · Foto in «Fotos» abgelegt' : 'Kommentar gespeichert' });
    } catch (err) { setError(err.message); }
  };

  return (
    <div className="narrow">
      <Stand abfragen={[kommentare]} />
      <form className="card" style={{ padding: 12 }} onSubmit={send}>
        <label className="field">
          <span className="vh">Kommentar</span>
          <textarea className="input" style={{ minHeight: 56 }} rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Kommentar zur Baustelle schreiben …" maxLength={2000} />
        </label>
        {foto && (
          <div className="row" style={{ padding: 8, borderRadius: 14, background: 'var(--bg)' }}>
            <img src={foto.preview} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover' }} />
            <span style={{ flex: 1, fontSize: 14, fontWeight: 700 }}>Foto angehängt</span>
            <button type="button" className="iconbtn white" aria-label="Foto entfernen" onClick={() => setFoto(null)}><Icon name="x" size={16} /></button>
          </div>
        )}
        <div className="row">
          <button type="button" className="btn secondary" onClick={() => fileInput.current?.click()}><Icon name="camera" />Foto</button>
          <input ref={fileInput} type="file" accept="image/*" capture="environment" hidden onChange={onFoto} />
          {error && <span className="error" role="alert">{error}</span>}
          <button type="submit" className="btn" style={{ marginLeft: 'auto' }} disabled={create.isPending}>{create.isPending ? 'Sendet …' : 'Senden'}</button>
        </div>
      </form>

      {kommentare.isLoading && <div className="skeleton" style={{ height: 120 }} />}
      {kommentare.isError && <div className="empty" role="alert">{kommentare.error.message}</div>}
      {!kommentare.isLoading && (kommentare.data ?? []).length === 0 && <div className="empty">Noch keine Kommentare. Halte Absprachen und Besonderheiten der Baustelle hier fest.</div>}

      {(kommentare.data ?? []).map((k) => (
        <article key={k.id} className="card" style={{ flexDirection: 'row', gap: 12, padding: '14px 16px' }}>
          <span className="avatar" style={{ width: 38, height: 38, fontSize: 13 }}>{initials(k.autor)}</span>
          <div className="stack" style={{ flex: 1, gap: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 800 }}>{k.autor} <span className="sub" style={{ fontWeight: 600 }}>· {dateTimeLabel(k.erstelltAm)}</span></span>
            {k.text && <p style={{ fontSize: 15, lineHeight: 1.45, color: 'var(--ink-2)', whiteSpace: 'pre-line' }}>{k.text}</p>}
            {k.foto && (
              <button className="photo" style={{ width: 200, maxWidth: '100%', aspectRatio: '4 / 3' }} onClick={() => setViewer(k.foto)} aria-label={`Foto ${k.foto.name} öffnen`}>
                <img src={fileUrl(k.foto.id)} alt="" loading="lazy" />
              </button>
            )}
          </div>
        </article>
      ))}

      {viewer && <FileViewer file={viewer} where={`${nr} / Fotos`} onClose={() => setViewer(null)} />}
    </div>
  );
}
